import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { FloppyDisk, UploadSimple, Plus, Trash, GraduationCap } from '@phosphor-icons/react';
import { API, BACKEND_URL } from './adminConstants';

export function CareersAdminManagement() {
  const { getAuthHeader } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});

  const load = async () => {
    try {
      const res = await axios.get(`${API}/page-content/careers_apprenticeships`);
      const data = res.data || {};
      // Backfill defaults so admin form is fully populated even after partial saves
      setContent({
        overline: data.overline || 'Progression',
        heading: data.heading || 'Apprenticeships &',
        heading_highlight: data.heading_highlight || 'Development',
        paragraphs: Array.isArray(data.paragraphs) && data.paragraphs.length ? data.paragraphs : [''],
        bullets: Array.isArray(data.bullets) ? data.bullets : [],
        image_1: data.image_1 || '',
        image_2: data.image_2 || '',
        image_3: data.image_3 || '',
        image_4: data.image_4 || '',
      });
    } catch {
      toast.error('Failed to load apprenticeships content');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(prev => ({ ...prev, [field]: true }));
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/services/upload-image`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      setContent(prev => ({ ...prev, [field]: `${BACKEND_URL}${res.data.image_url}` }));
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed');
    }
    setUploading(prev => ({ ...prev, [field]: false }));
    e.target.value = '';
  };

  const updateParagraph = (i, val) => {
    const p = [...content.paragraphs]; p[i] = val;
    setContent({ ...content, paragraphs: p });
  };
  const addParagraph = () => setContent({ ...content, paragraphs: [...content.paragraphs, ''] });
  const removeParagraph = (i) => setContent({ ...content, paragraphs: content.paragraphs.filter((_, idx) => idx !== i) });

  const updateBullet = (i, val) => {
    const b = [...content.bullets]; b[i] = val;
    setContent({ ...content, bullets: b });
  };
  const addBullet = () => setContent({ ...content, bullets: [...content.bullets, ''] });
  const removeBullet = (i) => setContent({ ...content, bullets: content.bullets.filter((_, idx) => idx !== i) });

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip empty trailing strings
      const payload = {
        ...content,
        paragraphs: content.paragraphs.filter(p => p && p.trim()),
        bullets: content.bullets.filter(b => b && b.trim()),
      };
      await axios.put(`${API}/admin/page-content/careers_apprenticeships`, payload, { headers: getAuthHeader() });
      toast.success('Apprenticeships section saved');
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-lime-600 border-t-transparent mx-auto"></div></div>;

  const renderImageField = (field, label) => (
    <div>
      <Label className="text-xs text-slate-700">{label}</Label>
      <div className="flex gap-3 mt-1">
        <div className="flex-1">
          <Input
            value={content[field] || ''}
            onChange={e => setContent({ ...content, [field]: e.target.value })}
            placeholder="Paste URL or upload..."
            data-testid={`apprenticeships-${field}-input`}
          />
        </div>
        <label className="cursor-pointer shrink-0">
          <Button variant="outline" size="sm" asChild disabled={uploading[field]}>
            <span><UploadSimple size={15} className="mr-1" />{uploading[field] ? 'Uploading…' : 'Upload'}</span>
          </Button>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => handleImageUpload(e, field)}
            data-testid={`apprenticeships-${field}-upload`}
          />
        </label>
      </div>
      {content[field] && (
        <div className="mt-2 w-40 h-24 rounded-lg overflow-hidden border border-slate-200">
          <img src={content[field]} alt="" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="careers-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-slate-900 flex items-center gap-2">
            <GraduationCap size={26} weight="duotone" className="text-lime-600" />
            Careers — Apprenticeships
          </h1>
          <p className="text-slate-500 text-sm">Edit the &quot;Apprenticeships &amp; Development&quot; section on the Careers page</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-lime-600 hover:bg-lime-700" data-testid="save-careers-btn">
          <FloppyDisk size={15} className="mr-1" />{saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Heading section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Heading</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Overline</Label>
              <Input
                value={content.overline}
                onChange={e => setContent({ ...content, overline: e.target.value })}
                placeholder="e.g. Progression"
                data-testid="apprenticeships-overline"
              />
            </div>
            <div>
              <Label className="text-xs">Heading</Label>
              <Input
                value={content.heading}
                onChange={e => setContent({ ...content, heading: e.target.value })}
                placeholder="e.g. Apprenticeships &"
                data-testid="apprenticeships-heading"
              />
            </div>
            <div>
              <Label className="text-xs">Heading Highlight (green accent)</Label>
              <Input
                value={content.heading_highlight}
                onChange={e => setContent({ ...content, heading_highlight: e.target.value })}
                placeholder="e.g. Development"
                data-testid="apprenticeships-heading-highlight"
              />
            </div>
          </div>
        </div>

        {/* Paragraphs */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Intro Paragraphs</h3>
            <Button variant="outline" size="sm" onClick={addParagraph} data-testid="add-paragraph-btn">
              <Plus size={14} className="mr-1" />Add Paragraph
            </Button>
          </div>
          <div className="space-y-3">
            {content.paragraphs.map((p, i) => (
              <div key={i} className="flex gap-2 items-start">
                <Textarea
                  value={p}
                  onChange={e => updateParagraph(i, e.target.value)}
                  rows={3}
                  className="flex-1"
                  data-testid={`apprenticeships-paragraph-${i}`}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeParagraph(i)}
                  className="text-red-500 border-red-200 hover:bg-red-50 shrink-0"
                  data-testid={`remove-paragraph-${i}`}
                >
                  <Trash size={14} />
                </Button>
              </div>
            ))}
            {content.paragraphs.length === 0 && <p className="text-sm text-slate-400 text-center py-2">No paragraphs yet</p>}
          </div>
        </div>

        {/* Bullets */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Bullet Points / Features</h3>
            <Button variant="outline" size="sm" onClick={addBullet} data-testid="add-bullet-btn">
              <Plus size={14} className="mr-1" />Add Bullet
            </Button>
          </div>
          <div className="space-y-2">
            {content.bullets.map((b, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input
                  value={b}
                  onChange={e => updateBullet(i, e.target.value)}
                  placeholder="e.g. On-the-job experience with formal qualifications"
                  data-testid={`apprenticeships-bullet-${i}`}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeBullet(i)}
                  className="text-red-500 border-red-200 hover:bg-red-50 shrink-0"
                  data-testid={`remove-bullet-${i}`}
                >
                  <Trash size={14} />
                </Button>
              </div>
            ))}
            {content.bullets.length === 0 && <p className="text-sm text-slate-400 text-center py-2">No bullets yet</p>}
          </div>
        </div>

        {/* Images */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Photos (4 images shown in a 2x2 grid)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {renderImageField('image_1', 'Image 1 (top-left)')}
            {renderImageField('image_2', 'Image 2 (top-right)')}
            {renderImageField('image_3', 'Image 3 (bottom-left)')}
            {renderImageField('image_4', 'Image 4 (bottom-right)')}
          </div>
        </div>
      </div>
    </div>
  );
}
